import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import { dayBounds, type DayMode } from "../../lib/businessDay";

// Един общ query параметър ?date= за всички справки: пази избрания период,
// така че той да оцелява при смяна на страница (/reports/:page — layout-ът
// навигира със запазен location.search).
// Формат: "YYYY-MM-DD" (един ден) или "YYYY-MM-DD_YYYY-MM-DD" (период).
// В URL-а се пази само денят — справките с избор на час (withTime) държат
// часа в локалния си state, без да го губят при писане в параметъра.
//
// Денят в параметъра е РАБОТНА дата (виж `lib/businessDay.ts`), не календарна.
// Затова краят се кодира през `businessDateOf`, а не с `format` върху самия
// момент: работният ден свършва в 05:59 на СЛЕДВАЩАТА календарна дата и
// „днес" би се записало като двудневен период, който при презареждане се
// прочита като два дни.
const PARAM = "date";
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const FMT = "YYYY-MM-DD";

const parseDay = (s: string | undefined): Dayjs | null => {
  if (!s || !DAY_RE.test(s)) return null;
  const d = dayjs(s);
  return d.isValid() ? d : null;
};

const parseParam = (
  raw: string | null,
  mode: DayMode,
): [Dayjs, Dayjs] | null => {
  if (!raw) return null;
  const [a, b] = raw.split("_");
  const start = parseDay(a);
  const end = parseDay(b ?? a);
  if (!start || !end || end.isBefore(start, "day")) return null;
  const { startOf, endOf } = dayBounds(mode);
  return [startOf(start), endOf(end)];
};

const encodeParam = (range: [Dayjs, Dayjs], mode: DayMode): string => {
  const { dateOf } = dayBounds(mode);
  const s = dateOf(range[0]).format(FMT);
  const e = dateOf(range[1]).format(FMT);
  return s === e ? s : `${s}_${e}`;
};

// Drop-in замяна на useState за dateRange в справките: чете началната
// стойност от ?date= (ако липсва/е невалиден — defaultRange, както досега)
// и при всяка промяна записва параметъра (replace — без нов history запис).
export function useReportsDateParam(
  defaultRange: () => [Dayjs, Dayjs],
  /** Календарен ден (по подразбиране) или работен — виж `lib/businessDay.ts`. */
  dayMode: DayMode = "calendar",
): [[Dayjs, Dayjs] | null, (r: [Dayjs, Dayjs] | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(
    () => parseParam(searchParams.get(PARAM), dayMode) ?? defaultRange(),
  );

  const setBoth = useCallback(
    (r: [Dayjs, Dayjs] | null) => {
      setRange(r);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (r) next.set(PARAM, encodeParam(r, dayMode));
          else next.delete(PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, dayMode],
  );

  // Синхронизация state ← URL при външна промяна на параметъра (напр. ръчно
  // редактиран адрес). Сравнява се кодираният ден-формат, така че смяна само
  // на часа в рамките на същите дни да не се презаписва.
  const raw = searchParams.get(PARAM);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  useEffect(() => {
    const current = rangeRef.current;
    if (raw === (current ? encodeParam(current, dayMode) : null)) return;
    const parsed = parseParam(raw, dayMode);
    if (parsed) setRange(parsed);
  }, [raw, dayMode]);

  return [range, setBoth];
}
